import { type TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { CollectionArrow } from '@sorare/core/src/atoms/icons/CollectionArrow';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { colorPerBonusRating } from 'lib/bonus';

import type { LineupBonusButton_so5Lineup } from './__generated__/index.graphql';

const Sup = styled.sup`
  font-size: var(--fs-10);
  text-transform: uppercase;
  color: var(--c-nd-600);
`;

type Props = { so5Lineup: LineupBonusButton_so5Lineup };

export const LineupBonusButton = ({ so5Lineup }: Props) => {
  if (!so5Lineup.bonusRating) {
    return null;
  }

  return (
    <Button
      size="compact"
      color="quaternary"
      startIcon={
        <CollectionArrow color={colorPerBonusRating[so5Lineup.bonusRating]} />
      }
    >
      {typeof so5Lineup.bonusPoints === 'number' ? (
        <Horizontal gap={0.5}>
          <FormattedMessage
            {...fantasy.ordinalPoints}
            values={{
              score: (
                <FormattedNumber
                  value={so5Lineup.bonusPoints}
                  signDisplay="always"
                />
              ),
              sup: c => <Sup>{c}</Sup>,
            }}
          />
        </Horizontal>
      ) : (
        <FormattedMessage {...fantasy.bonus} />
      )}
    </Button>
  );
};

LineupBonusButton.fragments = {
  so5Lineup: gql`
    fragment LineupBonusButton_so5Lineup on So5Lineup {
      id
      bonusPoints
      bonusRating
    }
  ` as TypedDocumentNode<LineupBonusButton_so5Lineup>,
};
